import { forwardRef, useImperativeHandle, useRef, useState, useCallback } from 'react';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

const PAYMENT_ELEMENT_OPTIONS = { layout: 'tabs' };

const StripePaymentForm = forwardRef(function StripePaymentForm({ onPaymentError, returnUrl, customerFullname, customerEmail }, ref) {
  const stripe = useStripe();
  const elements = useElements();

  const returnUrlRef = useRef(returnUrl);
  returnUrlRef.current = returnUrl;

  const onPaymentErrorRef = useRef(onPaymentError);
  onPaymentErrorRef.current = onPaymentError;

  const [cardholderName, setCardholderName] = useState(customerFullname || '');
  const [email] = useState(customerEmail || '');
  const [fieldErrors, setFieldErrors] = useState({ cardholderName: '', email: '' });

  const cardholderNameRef = useRef(customerFullname || '');
  const emailRef = useRef(customerEmail || '');

  const handleNameChange = useCallback((e) => {
    const val = e.target.value;
    setCardholderName(val);
    cardholderNameRef.current = val;
    if (val.trim()) {
      setFieldErrors((prev) => ({ ...prev, cardholderName: '' }));
    }
  }, []);

  useImperativeHandle(ref, () => ({
    validateFields: async () => {
      if (!stripe || !elements) return null;

      const name = cardholderNameRef.current.trim();
      const emailVal = emailRef.current.trim();
      const errors = { cardholderName: '', email: '' };
      let billingValid = true;

      if (!name) {
        errors.cardholderName = 'Cardholder name is required.';
        billingValid = false;
      }
      if (!emailVal) {
        errors.email = 'Email address is required.';
        billingValid = false;
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal)) {
        errors.email = 'Please enter a valid email address.';
        billingValid = false;
      }

      setFieldErrors(errors);

      if (!billingValid) return null;

      return true;
    },

    confirmPayment: async () => {
      if (!stripe || !elements) {
        onPaymentErrorRef.current('Stripe has not loaded yet — please try again.');
        return null;
      }
      try {
        const { error: submitError } = await elements.submit();
        if (submitError) {
          onPaymentErrorRef.current(submitError.message);
          return null;
        }

        const { error } = await stripe.confirmPayment({
          elements,
          confirmParams: {
            return_url: returnUrlRef.current || window.location.href,
            payment_method_data: {
              billing_details: {
                name: cardholderNameRef.current.trim(),
                email: emailRef.current.trim(),
              },
            },
          },
          redirect: 'if_required',
        });

        if (error) {
          onPaymentErrorRef.current(error.message);
          return null;
        }

        return true;
      } catch (err) {
        onPaymentErrorRef.current(err?.message || 'Payment failed. Please try again.');
        return null;
      }
    },
  }), [stripe, elements]);

  return (
    <div className="stripe-billing-fields">
      <div className="stripe-field-group">
        <label className="stripe-field-label" htmlFor="cardholder-name">Cardholder Name</label>
        <input
          id="cardholder-name"
          className={`stripe-field-input${fieldErrors.cardholderName ? ' stripe-field-input--error' : ''}`}
          type="text"
          placeholder="Full name on card"
          value={cardholderName}
          onChange={handleNameChange}
          autoComplete="cc-name"
          spellCheck={false}
        />
        {fieldErrors.cardholderName && (
          <span className="stripe-field-error" role="alert">{fieldErrors.cardholderName}</span>
        )}
      </div>

      <div className="stripe-field-group">
        <label className="stripe-field-label" htmlFor="billing-email">Email Address</label>
        <input
          id="billing-email"
          className={`stripe-field-input${fieldErrors.email ? ' stripe-field-input--error' : ''}`}
          type="email"
          value={email}
          readOnly
          autoComplete="email"
        />
        {fieldErrors.email && (
          <span className="stripe-field-error" role="alert">{fieldErrors.email}</span>
        )}
      </div>

      <PaymentElement options={PAYMENT_ELEMENT_OPTIONS} />
    </div>
  );
});

export default StripePaymentForm;
